import { DimeSelect, DimeSelectOption } from './DimeSelect.js';
import { cn } from './lib/utils.js';

export interface DimeDurationPickerProps {
  /** Given to the first select (days when `maxDays` is set, else hours), so a field label points at it. */
  id?: string;
  /** Accessible name of the hours select, for a picker with no visible label beside it. */
  hoursLabel?: string;
  /** Minutes, or null for no duration. */
  value: number | null;
  onValueChange: (value: number | null) => void;
  onBlur?: () => void;
  /** Spacing of the minute options. */
  minuteStep?: number;
  /** Last hour offered; ignored when `maxDays` is set. */
  maxHours?: number;
  /** Adds a days select in front, limits hours to 0-23 and caps the total at this many days. */
  maxDays?: number;
  disabled?: boolean;
  className?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const MINUTES_PER_DAY = MINUTES_PER_HOUR * HOURS_PER_DAY;
const DEFAULT_MINUTE_STEP = 30;
const DEFAULT_MAX_HOURS = 24;
const UNSET = '';

export function DimeDurationPicker({
  id,
  hoursLabel,
  value,
  onValueChange,
  onBlur,
  minuteStep = DEFAULT_MINUTE_STEP,
  maxHours = DEFAULT_MAX_HOURS,
  maxDays = 0,
  disabled = false,
  className,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}: Readonly<DimeDurationPickerProps>) {
  const withDays = maxDays > 0;
  const days = value == null || !withDays ? null : Math.floor(value / MINUTES_PER_DAY);
  const withinDay = value == null ? null : value - (days ?? 0) * MINUTES_PER_DAY;
  const hours = withinDay == null ? null : Math.floor(withinDay / MINUTES_PER_HOUR);
  const minutes = withinDay == null ? null : withinDay % MINUTES_PER_HOUR;
  const lastHour = withDays ? HOURS_PER_DAY - 1 : maxHours;
  // A saved value off the lists (e.g. 45 min, or 30 hours) still shows instead of reading as unset.
  const hourOptions = Array.from({ length: Math.max(lastHour, hours ?? 0) + 1 }, (_, hour) => hour);
  const dayOptions = Array.from({ length: maxDays + 1 }, (_, day) => day);
  const minuteOptions = Array.from(
    new Set([
      ...Array.from({ length: MINUTES_PER_HOUR / minuteStep }, (_, index) => index * minuteStep),
      ...(minutes == null ? [] : [minutes]),
    ]),
  ).sort((left, right) => left - right);

  const maxMinutes = withDays ? maxDays * MINUTES_PER_DAY : maxHours * MINUTES_PER_HOUR;
  const minutesBeforeMinutePart = (days ?? 0) * MINUTES_PER_DAY + (hours ?? 0) * MINUTES_PER_HOUR;

  // The lists end at the cap, so the total caps there too (24 hr, not 24 hr 30 min).
  function choose(nextDays: number, nextHours: number, nextMinutes: number) {
    const total = nextDays * MINUTES_PER_DAY + nextHours * MINUTES_PER_HOUR + nextMinutes;
    const capped = Math.min(total, maxMinutes);
    onValueChange(capped === 0 ? null : capped);
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {withDays && (
        <>
          <DimeSelect
            id={id}
            aria-label={id === undefined ? 'Days' : undefined}
            value={days == null ? UNSET : String(days)}
            disabled={disabled}
            aria-invalid={ariaInvalid}
            aria-describedby={ariaDescribedBy}
            onBlur={onBlur}
            onChange={(event) =>
              event.target.value === UNSET
                ? onValueChange(null)
                : choose(Number(event.target.value), hours ?? 0, minutes ?? 0)
            }
          >
            <DimeSelectOption value={UNSET}>–</DimeSelectOption>
            {dayOptions.map((day) => (
              <DimeSelectOption key={day} value={String(day)}>
                {day}
              </DimeSelectOption>
            ))}
          </DimeSelect>
          <span className="text-sm text-muted-foreground">day</span>
        </>
      )}
      <DimeSelect
        id={withDays ? undefined : id}
        aria-label={withDays ? (hoursLabel ?? 'Hours') : hoursLabel}
        value={hours == null ? UNSET : String(hours)}
        disabled={disabled}
        aria-invalid={ariaInvalid}
        aria-describedby={withDays ? undefined : ariaDescribedBy}
        onBlur={onBlur}
        onChange={(event) =>
          event.target.value === UNSET
            ? onValueChange(null)
            : choose(days ?? 0, Number(event.target.value), minutes ?? 0)
        }
      >
        <DimeSelectOption value={UNSET}>–</DimeSelectOption>
        {hourOptions.map((hour) => (
          <DimeSelectOption
            key={hour}
            value={String(hour)}
            disabled={
              withDays &&
              hour > 0 &&
              (days ?? 0) * MINUTES_PER_DAY + hour * MINUTES_PER_HOUR > maxMinutes
            }
          >
            {hour}
          </DimeSelectOption>
        ))}
      </DimeSelect>
      <span className="text-sm text-muted-foreground">hr</span>
      <DimeSelect
        aria-label="Minutes"
        value={minutes == null ? UNSET : String(minutes)}
        disabled={disabled}
        aria-invalid={ariaInvalid}
        onBlur={onBlur}
        onChange={(event) =>
          event.target.value === UNSET
            ? onValueChange(null)
            : choose(days ?? 0, hours ?? 0, Number(event.target.value))
        }
      >
        <DimeSelectOption value={UNSET}>–</DimeSelectOption>
        {minuteOptions.map((minute) => (
          <DimeSelectOption
            key={minute}
            value={String(minute)}
            disabled={value != null && minute > 0 && minutesBeforeMinutePart >= maxMinutes}
          >
            {String(minute).padStart(2, '0')}
          </DimeSelectOption>
        ))}
      </DimeSelect>
      <span className="text-sm text-muted-foreground">min</span>
    </div>
  );
}

DimeDurationPicker.displayName = 'DimeDurationPicker';
